import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, CheckCircle2, ChevronUp, Clock, Users, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { BranchPicker } from '../components/ui/BranchPicker';
import { EmptyState } from '../components/ui/EmptyState';
import { SearchInput } from '../components/ui/SearchInput';
import { Select } from '../components/ui/Select';
import { SelfieViewerModal } from '../components/ui/SelfieViewerModal';
import { StaffPicker } from '../components/ui/StaffPicker';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useRegions, useStaffList } from '../lib/options';
import { apiErrorMessage, useToast } from '../lib/ToastContext';
import { useDebounced } from '../lib/useDebounced';
import { Loader } from '../components/ui/Spinner';

type TodayRow = {
  userId: string; name: string; employeeCode: string; branchId: string; branchName: string; status: string;
  checkInAt: string | null; checkOutAt: string | null; checkInMode: 'geofence' | 'roaming' | 'wfh' | 'anywhere' | null;
  checkInSelfieUrl: string | null; checkOutSelfieUrl: string | null; checkInLat: string | null; checkInLng: string | null; checkOutLat: string | null; checkOutLng: string | null;
};
type ReportRow = { userId: string; name: string; employeeCode: string; present: number; late: number; halfDay: number; onLeave: number; absent: number; holiday: number };
type WfhRequest = { id: string; userId: string; fromDate: string; toDate: string; reason: string; status: string; reviewNote: string | null; createdAt: string };
type Correction = { id: string; userId: string; workDate: string; requestedCheckInAt: string | null; requestedCheckOutAt: string | null; reason: string; status: string };
type LeaveApp = { id: string; userId: string; leaveTypeId: string; startDate: string; endDate: string; days: string; halfDayPeriod: string | null; reason: string; status: string; escalatedAt: string | null; escalationNote: string | null; createdAt: string };
// Only non-geofence punches get a badge — a normal branch punch is the default and needs no label.
const MODE_BADGE: Record<string, string> = { wfh: 'WFH', roaming: 'Roaming', anywhere: 'Remote' };
const raisedAt = (v: string) => new Date(v).toLocaleDateString('en-IN', { dateStyle: 'medium' });
const daysLabel = (leave: Pick<LeaveApp, 'days' | 'halfDayPeriod'>) => `${leave.days} day(s)${leave.halfDayPeriod ? ` (${leave.halfDayPeriod === 'first_half' ? 'first half' : 'second half'})` : ''}`;

const STATUS_STYLE: Record<string, string> = { present: 'bg-brand-100 text-brand-700', late: 'bg-amber-100 text-amber-700', not_marked: 'bg-red-50 text-red-700', on_leave: 'bg-slate-100 text-slate-700', half_day: 'bg-amber-100 text-amber-700' };

function todayDate() { return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date()); }
function monthStart() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; }

function useNameLookup() {
  const staff = useStaffList();
  return useMemo(() => new Map((staff.data ?? []).map((s) => [s.id, `${s.name} (${s.employeeCode})`])), [staff.data]);
}

function TodayTab() {
  const [q, setQ] = useState('');
  const debouncedQ = useDebounced(q);
  const [branchId, setBranchId] = useState('');
  const [status, setStatus] = useState('');
  const [viewing, setViewing] = useState<TodayRow | null>(null);

  // Summary tiles always reflect the manager's whole scope; the list below is a separate,
  // server-filtered query so search/branch/status narrow the table without skewing the counts.
  const summaryQuery = useQuery({ queryKey: ['attendance', 'today'], queryFn: async () => (await api.get<TodayRow[]>('/api/attendance/today')).data, refetchInterval: 60_000 });
  const query = useQuery({
    queryKey: ['attendance', 'today', { q: debouncedQ, branchId, status }],
    queryFn: async () => (await api.get<TodayRow[]>('/api/attendance/today', { params: { q: debouncedQ || undefined, branchId: branchId || undefined, status: status || undefined } })).data,
    refetchInterval: 60_000,
  });
  const rows = query.data ?? [];

  const summary = useMemo(() => {
    const all = summaryQuery.data ?? [];
    return { total: all.length, present: all.filter((r) => r.status === 'present' || r.status === 'late').length, notMarked: all.filter((r) => r.status === 'not_marked').length };
  }, [summaryQuery.data]);

  return (
    <div>
      <div className="mb-4 grid grid-cols-3 gap-3">
        <div className="card py-3 text-center"><p className="text-2xl font-bold">{summary.total}</p><p className="text-xs text-muted">Total staff</p></div>
        <div className="card py-3 text-center"><p className="text-2xl font-bold text-brand-700">{summary.present}</p><p className="text-xs text-muted">Checked in</p></div>
        <div className="card py-3 text-center"><p className="text-2xl font-bold text-red-700">{summary.notMarked}</p><p className="text-xs text-muted">Not marked</p></div>
      </div>
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <SearchInput placeholder="Search staff…" value={q} onChange={(e) => setQ(e.target.value)} />
        <BranchPicker className="mt-0" clearLabel="All branches" placeholder="Filter by branch…" value={branchId} onChange={setBranchId} />
        <Select value={status} onChange={(e) => setStatus(e.target.value)}><option value="">All statuses</option><option value="present">Present</option><option value="late">Late</option><option value="not_marked">Not marked</option><option value="on_leave">On leave</option></Select>
      </div>

      {query.isLoading && <Loader />}
      {!query.isLoading && rows.length === 0 && <EmptyState title="No staff match these filters" />}

      <div className="space-y-3 lg:hidden">
        {rows.map((row) => (
          <article key={row.userId} className="card flex items-center justify-between gap-3">
            <div className="min-w-0">
              <strong className="block truncate">{row.name}</strong>
              <p className="mt-0.5 truncate text-sm text-muted">{row.employeeCode} • {row.branchName}</p>
            </div>
            <div className="shrink-0 text-right">
              <span className={`badge ${STATUS_STYLE[row.status] ?? ''}`}>{row.status.replace('_', ' ')}</span>
              {MODE_BADGE[row.checkInMode ?? ''] && <span className="badge ml-1 bg-slate-100 text-slate-700">{MODE_BADGE[row.checkInMode ?? '']}</span>}
              {row.checkInAt && <p className="mt-1 text-xs text-muted">In {new Date(row.checkInAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>}
              {row.checkInSelfieUrl && <button type="button" className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-brand-700" onClick={() => setViewing(row)}><Camera size={13} /> View selfie</button>}
            </div>
          </article>
        ))}
      </div>

      {rows.length > 0 && (
        <div className="hidden table-wrap lg:block">
          <table className="table">
            <thead><tr><th>Name</th><th>Branch</th><th>Status</th><th>Check-in</th><th></th></tr></thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.userId}>
                  <td><strong>{row.name}</strong><p className="text-xs text-muted">{row.employeeCode}</p></td>
                  <td className="text-muted">{row.branchName}</td>
                  <td><span className={`badge ${STATUS_STYLE[row.status] ?? ''}`}>{row.status.replace('_', ' ')}</span>{MODE_BADGE[row.checkInMode ?? ''] && <span className="badge ml-1 bg-slate-100 text-slate-700">{MODE_BADGE[row.checkInMode ?? '']}</span>}</td>
                  <td className="text-muted">{row.checkInAt ? new Date(row.checkInAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                  <td>{row.checkInSelfieUrl && <button type="button" className="btn-secondary !px-3" aria-label="View selfie" onClick={() => setViewing(row)}><Camera size={16} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewing && (
        <SelfieViewerModal
          title={viewing.name}
          subtitle={`${viewing.employeeCode} • ${viewing.branchName}`}
          checkIn={{ at: viewing.checkInAt, selfieUrl: viewing.checkInSelfieUrl, lat: viewing.checkInLat, lng: viewing.checkInLng }}
          checkOut={{ at: viewing.checkOutAt, selfieUrl: viewing.checkOutSelfieUrl, lat: viewing.checkOutLat, lng: viewing.checkOutLng }}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

const REQUEST_STATUS_STYLE: Record<string, string> = { pending: 'bg-amber-100 text-amber-700', approved: 'bg-brand-100 text-brand-700', rejected: 'bg-red-50 text-red-700', cancelled: 'bg-slate-100 text-slate-600' };

// The "Pending" sub-tab is the one a manager with hundreds of staff actually needs first —
// a bright count badge on it, and a separate "All" tab for history, instead of one long list
// that mixes both and makes pending requests hard to spot.
function SubTabs({ pendingCount, subTab, setSubTab }: { pendingCount: number; subTab: 'pending' | 'all'; setSubTab: (t: 'pending' | 'all') => void }) {
  return (
    <div className="mb-3 flex gap-2">
      <button className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold ${subTab === 'pending' ? 'bg-brand-600 text-white' : 'border border-border bg-white text-muted'}`} onClick={() => setSubTab('pending')}>
        Pending
        {pendingCount > 0 && <span className={`rounded-full px-1.5 py-0.5 text-xs font-bold ${subTab === 'pending' ? 'bg-white text-red-600' : 'bg-red-600 text-white'}`}>{pendingCount}</span>}
      </button>
      <button className={`rounded-full px-3 py-1.5 text-sm font-semibold ${subTab === 'all' ? 'bg-brand-600 text-white' : 'border border-border bg-white text-muted'}`} onClick={() => setSubTab('all')}>All</button>
    </div>
  );
}

function LeaveApprovalsSection({ names }: { names: Map<string, string> }) {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const regions = useRegions();
  const [subTab, setSubTab] = useState<'pending' | 'all'>('pending');
  const [staffId, setStaffId] = useState('');
  const [regionId, setRegionId] = useState('');
  // Pending count for the tab badge must reflect ALL pending requests in scope, not just the
  // ones matching the staff/region filters below — otherwise narrowing the filters would make
  // the badge lie about how much is actually waiting.
  const pendingCount = useQuery({ queryKey: ['leave-applications', 'manager-pending-count'], queryFn: async () => (await api.get<LeaveApp[]>('/api/leave-applications', { params: { status: 'pending' } })).data });
  const leaves = useQuery({
    queryKey: ['leave-applications', 'manager', { subTab, staffId, regionId }],
    queryFn: async () => (await api.get<LeaveApp[]>('/api/leave-applications', { params: { status: subTab === 'pending' ? 'pending' : undefined, userId: staffId || undefined, regionId: regionId || undefined } })).data,
  });

  const reviewLeave = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'approved' | 'rejected' }) => api.post(`/api/leave-applications/${id}/review`, { decision }),
    onSuccess: (_d, v) => { void client.invalidateQueries({ queryKey: ['leave-applications'] }); success(v.decision === 'approved' ? 'Leave approved' : 'Leave rejected'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not review leave application')),
  });
  const escalateLeave = useMutation({
    mutationFn: (id: string) => api.post(`/api/leave-applications/${id}/escalate`, {}),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['leave-applications'] }); success('Escalated to region head / HR'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not escalate')),
  });

  const rows = leaves.data ?? [];
  const pendingTotal = pendingCount.data?.length ?? 0;

  return (
    <section>
      <h2 className="mb-3 text-lg font-bold">Leave applications</h2>
      <SubTabs pendingCount={pendingTotal} subTab={subTab} setSubTab={setSubTab} />
      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <StaffPicker className="mt-0" clearLabel="All staff" placeholder="Filter by staff member…" value={staffId} onChange={setStaffId} />
        <Select value={regionId} onChange={(e) => setRegionId(e.target.value)}><option value="">All regions</option>{regions.data?.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</Select>
      </div>
      {leaves.isLoading && <Loader />}
      {!leaves.isLoading && rows.length === 0 && <EmptyState title={subTab === 'pending' ? 'No pending leave applications match these filters' : 'No leave applications match these filters'} />}

      <div className="space-y-3 lg:hidden">
        {rows.map((leave) => (
          <article key={leave.id} className="card">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <strong>{names.get(leave.userId) ?? leave.userId}</strong>
                <p className="mt-1 text-sm text-muted">{leave.startDate === leave.endDate ? leave.startDate : `${leave.startDate} → ${leave.endDate}`} • {daysLabel(leave)}</p>
                <p className="mt-1 text-xs text-muted">Raised {raisedAt(leave.createdAt)}</p>
                <p className="mt-1 text-sm">{leave.reason}</p>
                {leave.escalatedAt && <p className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-700"><ChevronUp size={14} /> Escalated{leave.escalationNote ? `: ${leave.escalationNote}` : ''}</p>}
              </div>
              <span className={`badge shrink-0 ${REQUEST_STATUS_STYLE[leave.status] ?? ''}`}>{leave.status}</span>
            </div>
            {leave.status === 'pending' && (
              <div className="mt-3 flex flex-wrap gap-2">
                <button className="btn-primary" disabled={reviewLeave.isPending} onClick={() => reviewLeave.mutate({ id: leave.id, decision: 'approved' })}><CheckCircle2 size={16} /> Approve</button>
                <button className="btn-secondary" disabled={reviewLeave.isPending} onClick={() => reviewLeave.mutate({ id: leave.id, decision: 'rejected' })}><XCircle size={16} /> Reject</button>
                {!leave.escalatedAt && <button className="btn-secondary" disabled={escalateLeave.isPending} onClick={() => escalateLeave.mutate(leave.id)}><ChevronUp size={16} /> Escalate</button>}
              </div>
            )}
          </article>
        ))}
      </div>

      {rows.length > 0 && (
        <div className="hidden table-wrap lg:block">
          <table className="table">
            <thead><tr><th>Staff</th><th>Dates</th><th>Days</th><th>Raised at</th><th>Reason</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {rows.map((leave) => (
                <tr key={leave.id}>
                  <td><strong>{names.get(leave.userId) ?? leave.userId}</strong></td>
                  <td className="text-muted">{leave.startDate === leave.endDate ? leave.startDate : `${leave.startDate} → ${leave.endDate}`}</td>
                  <td className="text-muted">{daysLabel(leave)}</td>
                  <td className="text-muted">{raisedAt(leave.createdAt)}</td>
                  <td className="max-w-xs truncate text-muted">{leave.reason}{leave.escalatedAt && <span className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-700"><ChevronUp size={12} /> Escalated</span>}</td>
                  <td><span className={`badge ${REQUEST_STATUS_STYLE[leave.status] ?? ''}`}>{leave.status}</span></td>
                  <td>
                    {leave.status === 'pending' && (
                      <div className="flex justify-end gap-2">
                        <button className="btn-secondary !px-3" aria-label="Approve" disabled={reviewLeave.isPending} onClick={() => reviewLeave.mutate({ id: leave.id, decision: 'approved' })}><CheckCircle2 size={16} /></button>
                        <button className="btn-secondary !px-3" aria-label="Reject" disabled={reviewLeave.isPending} onClick={() => reviewLeave.mutate({ id: leave.id, decision: 'rejected' })}><XCircle size={16} /></button>
                        {!leave.escalatedAt && <button className="btn-secondary !px-3" aria-label="Escalate" disabled={escalateLeave.isPending} onClick={() => escalateLeave.mutate(leave.id)}><ChevronUp size={16} /></button>}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function CorrectionApprovalsSection({ names }: { names: Map<string, string> }) {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const [subTab, setSubTab] = useState<'pending' | 'all'>('pending');
  const corrections = useQuery({ queryKey: ['corrections'], queryFn: async () => (await api.get<Correction[]>('/api/corrections')).data });

  const reviewCorrection = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'approved' | 'rejected' }) => api.post(`/api/corrections/${id}/review`, { decision }),
    onSuccess: (_d, v) => { void client.invalidateQueries({ queryKey: ['corrections'] }); void client.invalidateQueries({ queryKey: ['attendance'] }); success(v.decision === 'approved' ? 'Correction approved — attendance updated' : 'Correction rejected'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not review correction')),
  });

  const all = corrections.data ?? [];
  const pending = all.filter((c) => c.status === 'pending');
  const rows = subTab === 'pending' ? pending : all;
  const time = (v: string | null) => (v ? new Date(v).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—');

  return (
    <section>
      <h2 className="mb-3 text-lg font-bold">Missed-punch corrections</h2>
      <SubTabs pendingCount={pending.length} subTab={subTab} setSubTab={setSubTab} />
      {corrections.isLoading && <Loader />}
      {!corrections.isLoading && rows.length === 0 && <EmptyState title={subTab === 'pending' ? 'No pending correction requests' : 'No correction requests yet'} />}

      <div className="space-y-3 lg:hidden">
        {rows.map((c) => (
          <article key={c.id} className="card">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <strong>{names.get(c.userId) ?? c.userId}</strong>
                <p className="mt-1 text-sm text-muted">{c.workDate}</p>
                <p className="mt-1 text-sm">{c.reason}</p>
                <p className="mt-1 text-xs text-muted">Requested: {time(c.requestedCheckInAt)} to {time(c.requestedCheckOutAt)}</p>
              </div>
              <span className={`badge shrink-0 ${REQUEST_STATUS_STYLE[c.status] ?? ''}`}>{c.status}</span>
            </div>
            {c.status === 'pending' && (
              <div className="mt-3 flex flex-wrap gap-2">
                <button className="btn-primary" disabled={reviewCorrection.isPending} onClick={() => reviewCorrection.mutate({ id: c.id, decision: 'approved' })}><CheckCircle2 size={16} /> Approve</button>
                <button className="btn-secondary" disabled={reviewCorrection.isPending} onClick={() => reviewCorrection.mutate({ id: c.id, decision: 'rejected' })}><XCircle size={16} /> Reject</button>
              </div>
            )}
          </article>
        ))}
      </div>

      {rows.length > 0 && (
        <div className="hidden table-wrap lg:block">
          <table className="table">
            <thead><tr><th>Staff</th><th>Work date</th><th>Requested</th><th>Reason</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td><strong>{names.get(c.userId) ?? c.userId}</strong></td>
                  <td className="text-muted">{c.workDate}</td>
                  <td className="text-muted">{time(c.requestedCheckInAt)} – {time(c.requestedCheckOutAt)}</td>
                  <td className="max-w-xs truncate text-muted">{c.reason}</td>
                  <td><span className={`badge ${REQUEST_STATUS_STYLE[c.status] ?? ''}`}>{c.status}</span></td>
                  <td>
                    {c.status === 'pending' && (
                      <div className="flex justify-end gap-2">
                        <button className="btn-secondary !px-3" aria-label="Approve" disabled={reviewCorrection.isPending} onClick={() => reviewCorrection.mutate({ id: c.id, decision: 'approved' })}><CheckCircle2 size={16} /></button>
                        <button className="btn-secondary !px-3" aria-label="Reject" disabled={reviewCorrection.isPending} onClick={() => reviewCorrection.mutate({ id: c.id, decision: 'rejected' })}><XCircle size={16} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function WfhApprovalsSection({ names }: { names: Map<string, string> }) {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const [subTab, setSubTab] = useState<'pending' | 'all'>('pending');
  const requests = useQuery({ queryKey: ['wfh-requests'], queryFn: async () => (await api.get<WfhRequest[]>('/api/wfh-requests')).data });

  const review = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'approved' | 'rejected' }) => api.post(`/api/wfh-requests/${id}/review`, { decision }),
    onSuccess: (_d, v) => { void client.invalidateQueries({ queryKey: ['wfh-requests'] }); success(v.decision === 'approved' ? 'Work from home approved — they can check in without location for those days' : 'Request rejected'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not review request')),
  });

  const all = requests.data ?? [];
  const pending = all.filter((r) => r.status === 'pending');
  const rows = subTab === 'pending' ? pending : all;
  const range = (r: WfhRequest) => (r.fromDate === r.toDate ? r.fromDate : `${r.fromDate} → ${r.toDate}`);

  return (
    <section>
      <h2 className="mb-3 text-lg font-bold">Work-from-home requests</h2>
      <SubTabs pendingCount={pending.length} subTab={subTab} setSubTab={setSubTab} />
      {requests.isLoading && <Loader />}
      {!requests.isLoading && rows.length === 0 && <EmptyState title={subTab === 'pending' ? 'No pending work-from-home requests' : 'No work-from-home requests yet'} />}

      <div className="space-y-3 lg:hidden">
        {rows.map((r) => (
          <article key={r.id} className="card">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <strong>{names.get(r.userId) ?? r.userId}</strong>
                <p className="mt-1 text-sm text-muted">{range(r)}</p>
                <p className="mt-1 text-xs text-muted">Raised {raisedAt(r.createdAt)}</p>
                <p className="mt-1 text-sm">{r.reason}</p>
              </div>
              <span className={`badge shrink-0 ${REQUEST_STATUS_STYLE[r.status] ?? ''}`}>{r.status}</span>
            </div>
            {r.status === 'pending' && (
              <div className="mt-3 flex flex-wrap gap-2">
                <button className="btn-primary" disabled={review.isPending} onClick={() => review.mutate({ id: r.id, decision: 'approved' })}><CheckCircle2 size={16} /> Approve</button>
                <button className="btn-secondary" disabled={review.isPending} onClick={() => review.mutate({ id: r.id, decision: 'rejected' })}><XCircle size={16} /> Reject</button>
              </div>
            )}
          </article>
        ))}
      </div>

      {rows.length > 0 && (
        <div className="hidden table-wrap lg:block">
          <table className="table">
            <thead><tr><th>Staff</th><th>Dates</th><th>Raised at</th><th>Reason</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td><strong>{names.get(r.userId) ?? r.userId}</strong></td>
                  <td className="text-muted">{range(r)}</td>
                  <td className="text-muted">{raisedAt(r.createdAt)}</td>
                  <td className="max-w-xs truncate text-muted">{r.reason}</td>
                  <td><span className={`badge ${REQUEST_STATUS_STYLE[r.status] ?? ''}`}>{r.status}</span></td>
                  <td>
                    {r.status === 'pending' && (
                      <div className="flex justify-end gap-2">
                        <button className="btn-secondary !px-3" aria-label="Approve" disabled={review.isPending} onClick={() => review.mutate({ id: r.id, decision: 'approved' })}><CheckCircle2 size={16} /></button>
                        <button className="btn-secondary !px-3" aria-label="Reject" disabled={review.isPending} onClick={() => review.mutate({ id: r.id, decision: 'rejected' })}><XCircle size={16} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ApprovalsTab() {
  const names = useNameLookup();
  return (
    <div className="space-y-8">
      <LeaveApprovalsSection names={names} />
      <WfhApprovalsSection names={names} />
      <CorrectionApprovalsSection names={names} />
    </div>
  );
}

function ReportsTab() {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayDate());
  const [branchId, setBranchId] = useState('');
  const report = useQuery({
    queryKey: ['attendance', 'report', from, to, branchId],
    queryFn: async () => (await api.get<ReportRow[]>('/api/attendance/report', { params: { from, to, ...(branchId ? { branchId } : {}) } })).data,
  });
  return (
    <div>
      <div className="card mb-4 grid gap-3 sm:grid-cols-3">
        <label className="label">From<input type="date" className="field mt-1" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="label">To<input type="date" className="field mt-1" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <label className="label">Branch<BranchPicker clearLabel="All branches" value={branchId} onChange={setBranchId} /></label>
      </div>
      {report.isLoading && <Loader />}
      {report.data?.length === 0 && <EmptyState title="No staff in range" />}
      {report.data && report.data.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Staff</th><th>Present</th><th>Late</th><th>Half day</th><th>On leave</th><th>Absent</th></tr></thead>
            <tbody>
              {report.data.map((row) => (
                <tr key={row.userId}>
                  <td><strong>{row.name}</strong><p className="text-xs text-muted">{row.employeeCode}</p></td>
                  <td>{row.present}</td><td>{row.late}</td><td>{row.halfDay}</td><td>{row.onLeave}</td><td>{row.absent}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function TeamPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<'today' | 'approvals' | 'reports'>('today');
  const tabs = [['today', 'Today', Users], ['approvals', 'Approvals', Clock], ['reports', 'Reports', CheckCircle2]] as const;
  return (
    <>
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Team Status</h1>
        <p className="mt-1 text-muted">{user?.roles.includes('region_head') || user?.roles.includes('general_head') ? 'Everyone in your regions.' : 'Everyone at your branch.'}</p>
      </div>
      <div className="mb-6 flex gap-2 border-b border-border">
        {tabs.map(([key, label, Icon]) => (
          <button key={key} className={`flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-semibold ${tab === key ? 'border-brand-600 text-brand-700' : 'border-transparent text-muted'}`} onClick={() => setTab(key)}>
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>
      {tab === 'today' && <TodayTab />}
      {tab === 'approvals' && <ApprovalsTab />}
      {tab === 'reports' && <ReportsTab />}
    </>
  );
}
